import { Suspense, lazy } from 'react';
import { FileSearchIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { EmptyState } from '@/shared/components/EmptyState';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { FilePaneSkeleton } from './FilePaneSkeleton';
import { NotFoundState } from '@/shared/components/NotFoundState';
import type { FileEntry } from '@/lib/api/generated/model';
import { useFilePreviewContent } from '../hooks/useFilePreviewContent';
import { classifyPreviewability } from '../previewability';
import type { FileView } from '../searchParams';
import { FILE_VIEW_PANEL_ID, viewTabId } from '../viewIds';
import { HistoryPane } from './HistoryPane';
import { HtmlPreview } from './HtmlPreview';
import { ImagePreview } from './ImagePreview';
import { PdfPreview } from './PdfPreview';
import { PreviewHeader } from './PreviewHeader';
import { UnpreviewableNotice } from './UnpreviewableNotice';

/**
 * Both preview renderers pull in dependencies (`react-markdown`+`remark-gfm`,
 * `codemirror`+`@codemirror/language-data`) that most sessions never need —
 * a session that only opens binary/unpreviewable files should not pay for
 * either. The boundary lives here, at the routing call site, rather than in
 * `MarkdownPreview`/`CodePreview` themselves, matching `WorkflowGraphPage`'s
 * inline `lazy()` pattern.
 */
const MarkdownPreview = lazy(() =>
  import('./MarkdownPreview').then((module) => ({
    default: module.MarkdownPreview,
  })),
);
const CodePreview = lazy(() =>
  import('./CodePreview').then((module) => ({ default: module.CodePreview })),
);

const ALL_FILE_VIEWS: readonly FileView[] = ['preview', 'history'];

export function PreviewPane({
  workspaceId,
  entries = [],
  selectedFile,
  entry: resolvedEntry,
  views = ALL_FILE_VIEWS,
  view,
  onViewChange,
  onClose,
  closeLabel,
  openInFiles,
}: {
  workspaceId: string;
  /** Optional in consequence of `entry`; defaults to `[]`. */
  entries?: FileEntry[];
  selectedFile: string | undefined;
  /**
   * A pre-resolved entry. When present it is used directly and
   * `entries`/`selectedFile` are not consulted — a caller that already holds
   * the entry (or resolved it from a path outside the current listing) does
   * not have to fake a listing to get a preview.
   */
  entry?: FileEntry;
  /** Which of the two views the header offers. Defaults to both. */
  views?: readonly FileView[];
  view: FileView;
  onViewChange: (view: FileView) => void;
  onClose: () => void;
  /**
   * What to call the header's close control. Passed straight through to
   * `PreviewHeader`, which defaults it to the Files page's `Close file`.
   */
  closeLabel?: string;
  /** Passed straight through to `PreviewHeader` (spec 250). */
  openInFiles?: boolean;
}) {
  if (selectedFile === undefined && resolvedEntry === undefined) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <FileSearchIcon
          aria-hidden="true"
          className="text-muted-foreground size-6"
          strokeWidth={1.5}
        />
        <p className="text-muted-foreground max-w-prose text-sm">
          Select a file to preview it here.
        </p>
      </div>
    );
  }

  // A `file` search param naming an entry outside the current listing is a
  // stale link (renamed/moved/deleted, or a folder navigated away from) —
  // degrades to an explicit not-found state rather than erroring.
  const entry =
    resolvedEntry ??
    entries.find(
      (candidate) =>
        candidate.path === selectedFile && candidate.kind === 'file',
    );

  if (entry === undefined) {
    return (
      <NotFoundState
        title="File not found"
        description="This file does not exist in the current folder. It may have been renamed, moved, or deleted."
        action={
          <Button variant="outline" size="sm" onClick={onClose}>
            Back to the folder
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PreviewHeader
        workspaceId={workspaceId}
        entry={entry}
        views={views}
        view={view}
        onViewChange={onViewChange}
        onClose={onClose}
        closeLabel={closeLabel}
        openInFiles={openInFiles}
      />
      <div
        role="tabpanel"
        id={FILE_VIEW_PANEL_ID}
        aria-labelledby={viewTabId(view)}
        data-testid={view === 'history' ? 'history-pane' : 'preview-body'}
        className="flex min-h-0 flex-1 flex-col"
      >
        {view === 'history' ? (
          <HistoryPane workspaceId={workspaceId} path={entry.path} />
        ) : (
          <PreviewBody workspaceId={workspaceId} entry={entry} />
        )}
      </div>
    </div>
  );
}

function PreviewBody({
  workspaceId,
  entry,
}: {
  workspaceId: string;
  entry: FileEntry;
}) {
  const previewability = classifyPreviewability(entry);

  // These render from a URL and never fetch content as text, so they sit
  // ahead of `PreviewContent`, which decodes the response as text.
  if (previewability.kind === 'image') {
    return (
      <ImagePreview
        workspaceId={workspaceId}
        path={entry.path}
        name={entry.name}
      />
    );
  }

  if (previewability.kind === 'pdf') {
    return (
      <PdfPreview
        workspaceId={workspaceId}
        path={entry.path}
        name={entry.name}
      />
    );
  }

  // Also renders from a URL (a blob, in a sandboxed frame) rather than
  // decoding the response as text, so it belongs here with `image` and `pdf`
  // and ahead of `PreviewContent` — whose union stays `markdown | code | text`
  // so an `'html'` leaking down there is a type error, not a silent dump of
  // the markup as plain text.
  if (previewability.kind === 'html') {
    return (
      <HtmlPreview
        workspaceId={workspaceId}
        path={entry.path}
        name={entry.name}
      />
    );
  }

  if (previewability.kind === 'binary') {
    return (
      <UnpreviewableNotice
        workspaceId={workspaceId}
        path={entry.path}
        name={entry.name}
      />
    );
  }

  return (
    <PreviewContent
      workspaceId={workspaceId}
      entry={entry}
      previewability={previewability}
    />
  );
}

function PreviewContent({
  workspaceId,
  entry,
  previewability,
}: {
  workspaceId: string;
  entry: FileEntry;
  previewability:
    | { kind: 'markdown' }
    | { kind: 'code'; language: string }
    | { kind: 'text' };
}) {
  const { content, isPending, loadFailed, refetch } = useFilePreviewContent(
    workspaceId,
    entry.path,
    previewability,
  );
  const showPlaceholder = usePendingDisplay(isPending);

  if (isPending) {
    return showPlaceholder ? (
      // Fewer rows than the history pane: this stands for one document's opening lines,
      // not a list of revisions.
      <FilePaneSkeleton label="Loading preview" rows={8} />
    ) : null;
  }

  if (loadFailed || content === undefined) {
    return (
      <EmptyState
        className="m-3"
        description="This file's content could not be loaded."
        action={
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        }
      />
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-auto p-4">
      <Suspense
        fallback={<FilePaneSkeleton label="Loading preview" rows={8} />}
      >
        {previewability.kind === 'markdown' ? (
          <MarkdownPreview content={content} />
        ) : (
          <CodePreview
            content={content}
            language={
              previewability.kind === 'code'
                ? previewability.language
                : undefined
            }
          />
        )}
      </Suspense>
    </div>
  );
}
